import { TagItemList, Widget, WidgetDeviceView, WidgetSimpleInfo, WidgetState } from '../campaign/widgets.model';
import { Account, CustomProfileProperty } from '../back-office/account.model';
import { Audience, AudienceSource, ExampleAudienceConfig } from '../audience/components/audience.model';
import { SessionCondition, SessionConditionGroup } from '../audience/components/session-condition-group.model';
import { SubscriptionDetails } from '../subscription/subscription.model';
import { CampaignFilterRequest, CampaignStatus, CampaignTag, OnsiteCampaign } from 'app/campaign/campaign.model';
import { CustomerModel } from 'app/account-settings/models/customer.model';
import { SiteCheckStatus } from 'app/account-settings/models/site-check.model';
import { PageTitle } from '../shared/utils/page-title';
import { CampaignTemplate } from 'app/campaign/templates/template.model';
import { Tier } from 'app/shared/account/tier.model';
import { OpportunityDetail } from '../new-primary-dashboard/new-primary-dashboard.models';
import { Currency } from '../shared/account/Currency';
import {
  DataPlatformVisitRow,
  DataPlatformProfileRow,
  DataPlatformEventFilter,
  DataPlatformContactRow,
} from '../data-platform/data-platform.model';

export class FlyoutOpeningData {
  type: FlyoutDataType;
  sessionId?: number;
  siteId?: number;
  uid?: number;
  organization?: string;
  netName?: string;
  identifier?: string;
  name?: string;
  commitment?: number;
  countryCode?: string;
  gravatarHash?: string;
  widgets?: Array<WidgetSimpleInfo>;
  accountDetails?: Account;
  isRemovable?: boolean;
  widgetId?: number;
  widget?: Widget;
  audience?: Audience;
  exampleAudienceConfig?: ExampleAudienceConfig;
  sessionConditionGroup?: SessionConditionGroup;
  sessionConditionGroupId?: number;
  sessionConditions?: Array<SessionCondition>;
  sessionConditionGroups?: Array<SessionConditionGroup>;
  // Why the audience can't get session conditions here (translation key), e.g. on native-app campaigns.
  sessionConditionsUnavailableKey?: string;
  // `{page}` of the DataPlatform feature tags the flyout sends.
  featurePage?: string;
  // The page period the audience reach follows; without it, a fixed trailing window.
  reachPageFilter?: DataPlatformEventFilter;
  activeApps?: Array<string>;
  tagItem?: TagItemList;
  tagIsCreate?: boolean;
  tags?: Array<string>;
  automationId?: string;
  subscriptionDetails?: SubscriptionDetails;
  subscriptionExists?: boolean;
  subscriptionCurrency?: Currency;
  responseListId?: string;
  width?: number;
  source?: AudienceSource;
  widgetState?: WidgetState;
  campaignName?: string;
  campaignStatus?: CampaignStatus;
  isTemplate?: boolean;
  currentCustomer?: CustomerModel;
  currentAccount?: Account;
  campaignReferral?: OnsiteCampaign;
  subType?: string;
  expandedTab?: string;
  dataSubmittedForm?: any;
  action?: any;
  triggers?: any;
  campaignRequest?: CampaignFilterRequest;
  campaignTags?: Array<CampaignTag>;
  transparentBackDrop?: boolean;
  filterId?: number;
  pageTitle?: PageTitle;
  isAdmin?: boolean;
  isBackoffice?: boolean;
  backOfficeAccounts?: Array<Account>;
  widgetPreviewContext?: FlyoutWidgetPreviewContext;
  tier?: Tier;
  listsIntegrations?: { IntegrationType: string };
  customProfileFields?: CustomProfileProperty[];
  dataPlatformVisitRow?: DataPlatformVisitRow;
  dataPlatformProfileRow?: DataPlatformProfileRow;
  dataPlatformContactRow?: DataPlatformContactRow;
  dataPlatformFilter?: DataPlatformEventFilter;
  /** Set when a visit flyout is opened from a profile flyout; shows a "back to profile" crumb. */
  dataPlatformBackTitle?: string;
  /** Set when a profile flyout is opened from a visit flyout; shows a "back to visit" crumb. */
  dataPlatformBackVisitRow?: DataPlatformVisitRow;
  /** The profile's visits (newest first) when a visit flyout is opened from a profile; enables
   *  stepping between the person's visits without going back to the profile. */
  dataPlatformVisitSiblings?: DataPlatformVisitRow[];
  hasFieldsMappingAction?: boolean;
  widgetSelectorItems?: WidgetSimpleInfo[];
  widgetSelectorSelectedIds?: number[];
  widgetSelectorOptionName?: string;
  widgetSelectorThumbnails?: [number, string][];
  holdoutEnabled?: boolean;
  holdoutPercentage?: number;
  kpiDefinitions?: KpiDefinitionData[];
  kpiSelectedKeys?: string[];
  /** Default metric keys to restore when the user clicks "Reset to default". Omit to hide the button. */
  kpiDefaultKeys?: string[];
  /** Whether the current metric selection is user-customized (vs. the auto/type-based default). Drives
   * the subtitle wording; only meaningful when `kpiDefaultKeys` is provided. */
  kpiCustomized?: boolean;
  /** The observation the recommendation flyout is opened for; its message becomes the heading. */
  recommendationHeadingKey?: string;
  recommendationHeadingParams?: Readonly<Record<string, string | number>>;
  recommendationDetail?: OpportunityDetail;
  /** Optional metric grouping: when provided, the flyout splits metrics into labelled groups
   * (each with its own added + available list) instead of one flat list. Metrics are assigned to a
   * group via `KpiDefinitionData.group`. Omit for a single flat list. */
  metricGroups?: KpiMetricGroup[];
  /** Optional second "sections" list in the KPI-settings flyout (toggle page sections on/off). Omit to hide it. */
  sectionDefinitions?: KpiDefinitionData[];
  sectionSelectedKeys?: string[];
  /** Default section keys restored alongside metrics by "Reset to default". Omit to leave sections as-is. */
  sectionDefaultKeys?: string[];
  siteCheckStatus?: SiteCheckStatus;
  siteCheckActiveDimensions?: string[];
  /** Empty means applying leaves the checkboxes alone. */
  siteCheckAppliedDimensions?: string[];
  siteCheckCategoryLevel?: number;
}

export enum FlyoutDataType {
  Contact = 'Contact',
  Session = 'Session',
  Audience = 'Audience',
  AudienceHub = 'AudienceHub',
  UpgradeAccount = 'UpgradeAccount',
  SessionConditionGroup = 'SessionConditionGroup',
  TagsSetting = 'TagsSetting',
  SessionConditionsOnWidget = 'SessionConditionsOnWidget',
  AutomationHistory = 'AutomationHistory',
  EditResponseList = 'EditResponseList',
  FormSettings = 'FormSettings',
  Referral = 'Referral',
  FormResponses = 'FormResponses',
  Automation = 'Automation',
  CampaignFilter = 'CampaignFilter',
  BrandSettings = 'BrandSettings',
  WidgetPreview = 'WidgetPreview',
  CopyAccountSettings = 'CopyAccountSettings',
  DataPlatformVisit = 'DataPlatformVisit',
  DataPlatformProfile = 'DataPlatformProfile',
  DataPlatformContact = 'DataPlatformContact',
  WidgetSelectorFilter = 'WidgetSelectorFilter',
  HoldoutSettings = 'HoldoutSettings',
  KpiSettings = 'KpiSettings',
  Recommendation = 'Recommendation',
  SiteCheckRecommendations = 'SiteCheckRecommendations',
}

/** A labelled metric group in the KPI-settings flyout (e.g. "Campaign Performance Metrics"). */
export interface KpiMetricGroup {
  key: string;
  labelKey: string;
}

export interface KpiDefinitionData {
  key: string;
  labelKey: string;
  tooltipKey?: string;
  format: 'number' | 'percent' | 'currency';
  placeholder?: boolean;
  /** Which `KpiMetricGroup` this metric belongs to when the flyout renders grouped. */
  group?: string;
  // Marks a KPI whose calculation isn't fully verified yet. Shown as an "Experimental" tag
  // in the customize-metrics flyout.
  experimental?: boolean;
  /** Section only: renders the toggle disabled (not selectable), with `disabledNoteKey` as explainer. */
  disabled?: boolean;
  /** Section only: i18n key for the muted explainer shown when `disabled` is true. */
  disabledNoteKey?: string;
}

export interface KpiSettingsResponse {
  selectedKeys: string[];
  /** Present only when the flyout was opened with a sections list (e.g. campaign performance). */
  selectedSectionKeys?: string[];
  /** True when this selection came from the "Reset to default" button (opener may clear customization). */
  resetToDefault?: boolean;
}

export interface PersonalData {
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  organization?: string;
  phone?: string;
  title?: string;
}

export interface PersonDetails {
  uids?: Array<number>;
  personalData?: PersonalData;
  properties?: any;
  commitment?: number;
  totalVisits?: number;
  uniqueVisitors?: number;
  firstSeen?: string;
  lastSeen?: string;
  interestProfiles?: Array<InterestProfileStatistics>;
  tags?: Array<string>;
  goals?: Array<VisitGoal>;
  ocEvents?: Array<OnsiteCampaignEvent>;
  campaignEvents?: Array<VisitEvent>;
  consents?: Array<ConsentEvent>;
  devices?: Array<VisitDevice>;
  visits?: Array<Visit>;
  // Calculated properties from existing ones
  calculatedDetails?: CalculatedSessionDetails;
  widgets?: Array<OnsiteCampaignEvent>;
  scores: Array<any>;
  revenue?: number;
  previousVisits?: number;
}

export interface SessionDetails {
  id?: number;
  uid?: number;
  time: string;
  userAgent?: string;
  timeOnSite?: number;
  lastUpdated?: number;
  webBrowser?: string;
  operatingSystem?: string;
  screenResolution?: string;
  timeTotal?: number;
  countryCode: string;
  geoLocation: string;
  goals?: Array<VisitGoal>;
  identifier: string;
  personalData: PersonalData;
  properties?: { string: string };
  tags?: Array<string>;
  identityTags?: Array<string>;
  actionsList?: Array<SessionAction>;
  consents?: Array<ConsentEvent>;
  ocEvents?: Array<OnsiteCampaignEvent>;
  custom?: { string: string };
  netName?: string;
  interestProfiles?: Array<InterestProfileStatistics>;
  // Calculated properties from existing ones
  calculatedDetails?: CalculatedSessionDetails;
  referrerDomain?: string;
  referrerType?: string;
  utm?: SessionUtm;
  widgets?: OnsiteCampaignEvent[];
  distributedCoupons?: Array<DistributedCoupon>;
}

export interface DistributedCoupon {
  couponSetId: string | null;
  code: string;
  isCopied: boolean;
}

export interface OnsiteCampaignEvent {
  type?: string;
  time?: string;
  id?: string;
  campaignName?: string;
  count?: number;
}

export interface VisitGoal {
  revenue?: string;
  time?: string;
  id?: string;
  count?: number;
}

export interface SessionUtm {
  campaignName?: string;
  medium?: string;
  source?: string;
  time?: string;
}

export interface CalculatedSessionDetails {
  onsiteCampaignStats: Array<OnsiteCampaignStatistics>;
  goals: Array<CalculatedDataEntry>;
  ocEvents: Array<OnsiteCampaignEvent>;
  widgetOpens: Array<CalculatedDataEntry>;
  widgetConversions: Array<CalculatedDataEntry>;
  clicks: Array<CalculatedDataEntry>;
  scrolls: Array<CalculatedDataEntry>;
  pageviews: Array<CalculatedDataEntry>;
  widgetClickthroughs: Array<CalculatedDataEntry>;
  widgetDismissals: Array<CalculatedDataEntry>;
}

export interface ConsentEvent {
  currentWebsiteUrl?: string;
  isCheckboxPresent?: boolean;
  privacyPolicyText?: string;
  privacyPolicyUrl?: string;
  revisionNumber?: number;
  time?: string;
  title?: string;
  versionDate?: string;
  // Calculated
  timestamp?: number;
  formattedTimestamp?: string;
  formattedVersionDate?: string;
}

export interface DisplayConsents {
  latestConsent?: ConsentEvent;
  firstConsent?: ConsentEvent;
  timesConsented?: number;
}

export interface OnsiteCampaignStatistics {
  widgetIds?: Array<number>;
  campaignName?: string;
  campaignId?: string;
  views?: number;
  clicks?: number;
  conversions?: number;
  conversionRate?: number;
  clickthroughs?: number;
  ctr?: number;
}

export interface CalculatedDataEntry {
  name?: string;
  count?: number;
  urls?: Array<string>;
  timestamps?: Array<number>;
  timestamp?: number;
  formattedTimestamp?: string;
  widgetId?: number;
  widgetIds?: Array<number>;
  campaignId?: number;
}

export interface SessionAction {
  time?: string;
  type?: SessionActionType;
  url?: string;
  title?: string;
  // Parsed value
  formattedTime?: string;
  duration?: number;
  widgetId?: number;
  eventId?: number;
  fullUrl?: string;
  scrollDown?: boolean;
}

export enum SessionActionType {
  WidgetOpen = 'widget-open',
  Pageview = 'pageview',
  Click = 'click',
  WidgetConversion = 'widget-conversion',
  Goal = 'goal',
  WidgetClickthrough = 'widget-clickthrough',
  WidgetDismissal = 'widget-dismissal',
  Event = 'event',
  Scroll = 'scroll',
  Outbound = 'outbound',
  PartialSubmit = 'widget-partial-submit',
  PartialSubmitSurvey = 'widget-survey-response',
  Purchase = 'purchase',
}
export enum ActionEventTitle {
  Survey = 'widget-survey-response-partial',
  Partial = 'widget-conversion-partial',
}

export enum OnsiteCampaignEventType {
  WidgetOpen = 'Open',
  WidgetConversion = 'Conversion',
  WidgetClickThrough = 'ClickThrough',
  CopyCoupon = 'CopyCoupon',
  SurveyResponse = 'SurveyResponse',
}

export interface VisitEvent {
  name?: string;
  time?: string;
  sessionId?: number;
}

export interface VisitDevice {
  time?: string;
  screenResolution?: string;
  browser?: string;
  operatingSystem?: string;
}

export interface InterestProfileStatistics {
  id?: string;
  value?: number;
  name?: string;
}

export interface Visit {
  time?: string;
  sessionId?: number;
  timeOnSite?: number;
  engagement?: number;
  organization?: string;
  interestProfiles: Array<InterestProfileStatistics>;
  // Calculated
  timestamp?: number;
  formattedTimestamp?: string;
  referrerDomain?: string;
  utmSource?: string;
  views?: number;
  source?: string;
  distributedCoupons?: Array<DistributedCoupon>;
}

export interface ManageTagRequest {
  type: FlyoutDataType;
  sessionId: number;
  uid: number;
  identifier: string;
  organization: string;
}

export interface SessionHistory {
  siteId: number;
  sessionId: number;
  identifier: string;
  email: string;
  automationId: string;
  newestExecutionTime: Date;
  events: Event[];
  totalNumberOfActions: number;
  actionsDone: number;
  allDone: boolean;
  waiting: boolean;
  failed: boolean;
  warning: boolean;
  aborted: boolean;
  unknownState: boolean;
}

export interface Event {
  executionTime: Date;
  stackTrace: string;
  innerExceptionMessage: string;
  innerExceptionStackTrace: string;
  implementationName: string;
  actionName: string;
  action: string;
  actionState: ActionState;
  message: string;
  showException: boolean;
  actionNameTranslationKey: string;
  automationNameTranslationKey: string;
}

export enum ActionState {
  New = 'New',
  Waiting = 'Waiting',
  Done = 'Done',
  Error = 'Error',
  Aborted = 'Aborted',
  Warning = 'Warning',
}

export interface FlyoutWidgetPreviewContext {
  widgetId: number;
  campaignId: number;
  siteId?: number;
  /** Renders the preview against the campaign's own account instead of the active one. */
  previewSiteId?: number;
  filteredTemplates: CampaignTemplate[];
  purposeId?: number;
  deviceView?: WidgetDeviceView;
}

export interface BrandSettingsCopyRequest {
  siteIdsToCopyTo: number[];
  shouldCopyFonts: boolean;
  shouldCopyColors: boolean;
  shouldCopyBrandVariables: boolean;
  shouldCopyTexts: boolean;
  shouldReplaceTextVariables: boolean;
  shouldCopyImages: boolean;
}
